import { useState, useEffect, useRef, useCallback } from "react";
import {
  X,
  ChevronDown,
  ChevronUp,
  Eye,
  Zap,
  Sparkles,
} from "lucide-react";
import Swal from "sweetalert2";
import { reglasNotificacionService } from "../../services/api";

const CANALES = [
  { value: "EMAIL", label: "Correo Electrónico" },
  { value: "SMS", label: "SMS" },
  { value: "WHATSAPP", label: "WhatsApp", disabled: true },
  { value: "PUSH", label: "Push", disabled: true },
];

const DESTINATARIOS = [
  { value: "conductor",                    label: "Conductor del viaje" },
  { value: "TRANSPORTISTA:admin",          label: "Transportista — Admin" },
  { value: "TRANSPORTISTA:transportista",  label: "Transportista — Usuario transportista" },
  { value: "OPERADOR:admin",               label: "Operador Logístico — Admin" },
  { value: "OPERADOR:operador_logistico",  label: "Operador Logístico — Usuario operador" },
  { value: "CLIENTE:admin",               label: "Cliente — Admin" },
  { value: "CLIENTE:cliente",             label: "Cliente — Usuario cliente" },
];

const IMPORTANCIAS = [
  { value: "BAJA", label: "Baja", color: "bg-gray-100 text-gray-700" },
  { value: "MEDIA", label: "Media", color: "bg-blue-100 text-blue-700" },
  { value: "ALTA", label: "Alta", color: "bg-yellow-100 text-yellow-700" },
  { value: "CRITICA", label: "Crítica", color: "bg-red-100 text-red-700" },
];

// Plantillas sugeridas por tipo de evento (mensaje listo para usar o editar)
const PLANTILLAS_SUGERIDAS = {
  VIAJE_INICIADO:
    "El viaje {viaje_codigo} ha iniciado.\nConductor: {conductor_completo}\nVehículo: {vehiculo_placa}\nFecha: {fecha_hora_actual}",
  VIAJE_FINALIZADO:
    "El viaje {viaje_codigo} ha finalizado.\nConductor: {conductor_completo}\nVehículo: {vehiculo_placa}\nFecha: {fecha_hora_actual}",
  PARADA_PROLONGADA:
    "⚠️ Alerta: el vehículo {vehiculo_placa} lleva más de 10 minutos detenido.\nViaje: {viaje_codigo}\nConductor: {conductor_completo}",
  LLEGADA_TARDE:
    "⏰ El viaje {viaje_codigo} ha llegado tarde a destino.\nConductor: {conductor_completo}\nVehículo: {vehiculo_placa}",
  TIEMPO_EXCEDIDO:
    "⚠️ El viaje {viaje_codigo} ha superado el tiempo estimado.\nConductor: {conductor_completo}\nVehículo: {vehiculo_placa}",
  ETA_RETRASADA:
    "El tiempo estimado de llegada del viaje {viaje_codigo} se ha retrasado.\nConductor: {conductor_completo}",
  ENTRADA_ESTABLECIMIENTO:
    "El vehículo {vehiculo_placa} ingresó a {establecimiento_nombre}.\nViaje: {viaje_codigo}\nHora: {hora_actual}",
  SALIDA_ESTABLECIMIENTO:
    "El vehículo {vehiculo_placa} salió de {establecimiento_nombre}.\nViaje: {viaje_codigo}\nHora: {hora_actual}",
  DESVIO_RUTA:
    "🚨 El vehículo {vehiculo_placa} se desvió de la ruta.\nViaje: {viaje_codigo}\nConductor: {conductor_completo}",
};

const FORM_INICIAL = {
  tipo_evento: "",
  canal: "EMAIL",
  destinatario_tipo: "TRANSPORTISTA:admin",
  importancia: "MEDIA",
  plantilla_mensaje: "",
  activo: true,
};

// Etiquetas amigables para cada variable técnica
const ETIQUETA_VARIABLE = {
  "{viaje_codigo}": "Código del viaje",
  "{codigo_viaje}": "Código del viaje",
  "{tipo_operacion}": "Tipo de operación",
  "{conductor_nombre}": "Nombre conductor",
  "{conductor_apellidos}": "Apellidos conductor",
  "{conductor_completo}": "Nombre completo conductor",
  "{conductor_telefono}": "Teléfono conductor",
  "{vehiculo_placa}": "Placa del vehículo",
  "{vehiculo_marca}": "Marca del vehículo",
  "{vehiculo_modelo}": "Modelo del vehículo",
  "{vehiculo_completo}": "Marca y modelo",
  "{establecimiento_nombre}": "Nombre establecimiento",
  "{establecimiento_tipo}": "Tipo establecimiento",
  "{establecimiento_direccion}": "Dirección establecimiento",
  "{empresa_transportista}": "Empresa transportista",
  "{empresa_cliente}": "Empresa cliente",
  "{empresa_operador}": "Operador logístico",
  "{tipo_evento}": "Tipo de evento",
  "{descripcion}": "Descripción del evento",
  "{fecha_evento}": "Fecha del evento",
  "{fecha_evento_largo}": "Fecha completa del evento",
  "{hora_actual}": "Hora actual",
  "{fecha_actual}": "Fecha actual",
  "{fecha_hora_actual}": "Fecha y hora actual",
  "{latitud}": "Latitud del vehículo",
  "{longitud}": "Longitud del vehículo",
};

export default function ReglaDrawer({ isOpen, onClose, regla, onSaved }) {
  const [form, setForm] = useState(FORM_INICIAL);
  const [tiposEventos, setTiposEventos] = useState({});
  const [variables, setVariables] = useState({});
  const [variablesOpen, setVariablesOpen] = useState(false);
  const [preview, setPreview] = useState("");
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [saving, setSaving] = useState(false);
  const textareaRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    reglasNotificacionService.tiposEventos().then((data) => setTiposEventos(data ?? {})).catch(() => {});
    reglasNotificacionService.variablesPlantilla().then((data) => setVariables(data ?? {})).catch(() => {});
  }, [isOpen]);

  useEffect(() => {
    if (regla) {
      setForm({
        tipo_evento: regla.tipo_evento || "",
        canal: regla.canal || "EMAIL",
        destinatario_tipo: regla.destinatario_tipo || "supervisor",
        importancia: regla.importancia || "MEDIA",
        plantilla_mensaje: regla.plantilla_mensaje || "",
        activo: regla.activo ?? true,
      });
    } else {
      setForm(FORM_INICIAL);
    }
    setPreview("");
  }, [regla, isOpen]);

  const fetchPreview = useCallback(async (plantilla, tipo_evento) => {
    if (!plantilla || !tipo_evento) { setPreview(""); return; }
    setLoadingPreview(true);
    try {
      const data = await reglasNotificacionService.previsualizarMensaje({ plantilla_mensaje: plantilla, tipo_evento });
      setPreview(data?.mensaje_renderizado || data?.mensaje || "");
    } catch {
      setPreview("");
    } finally {
      setLoadingPreview(false);
    }
  }, []);

  const handleChange = (field, value) => {
    const next = { ...form, [field]: value };
    setForm(next);

    if (field === "plantilla_mensaje" || field === "tipo_evento") {
      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        fetchPreview(next.plantilla_mensaje, next.tipo_evento);
      }, 600);
    }
  };

  const usarPlantillaSugerida = () => {
    const sugerida = PLANTILLAS_SUGERIDAS[form.tipo_evento];
    if (!sugerida) return;
    handleChange("plantilla_mensaje", sugerida);
  };

  const insertarVariable = (varKey) => {
    const ta = textareaRef.current;
    // varKey llega como "{viaje_codigo}" — insertamos sin llaves extra
    const token = varKey; // ya tiene las llaves
    if (!ta) return;
    const inicio = ta.selectionStart;
    const fin = ta.selectionEnd;
    const texto = form.plantilla_mensaje;
    const nuevo = texto.slice(0, inicio) + token + texto.slice(fin);
    handleChange("plantilla_mensaje", nuevo);
    setTimeout(() => {
      ta.focus();
      ta.setSelectionRange(inicio + token.length, inicio + token.length);
    }, 0);
  };

  const handleGuardar = async () => {
    if (!form.tipo_evento) {
      Swal.fire({ icon: "warning", title: "Falta el tipo de evento", toast: true, position: "top-end", showConfirmButton: false, timer: 3000 });
      return;
    }
    if (!form.plantilla_mensaje.trim()) {
      Swal.fire({ icon: "warning", title: "Falta la plantilla del mensaje", toast: true, position: "top-end", showConfirmButton: false, timer: 3000 });
      return;
    }
    setSaving(true);
    try {
      if (regla) {
        await reglasNotificacionService.update(regla.id, form);
      } else {
        await reglasNotificacionService.create(form);
      }
      Swal.fire({ icon: "success", title: regla ? "Regla actualizada" : "Regla creada", toast: true, position: "top-end", showConfirmButton: false, timer: 2500 });
      onSaved();
      onClose();
    } catch (err) {
      const msg = err.response?.data?.message || "Error al guardar la regla";
      Swal.fire({ icon: "error", title: msg, toast: true, position: "top-end", showConfirmButton: false, timer: 3500 });
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const tienePlantillaSugerida = !!PLANTILLAS_SUGERIDAS[form.tipo_evento];

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />

      {/* Panel */}
      <div className="relative z-10 w-full max-w-xl h-full bg-white shadow-2xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b bg-gray-50">
          <h2 className="text-lg font-semibold text-gray-800">
            {regla ? "Editar regla" : "Nueva regla de notificación"}
          </h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-200 transition">
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {/* Tipo de evento */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              ¿Cuándo se envía? <span className="text-red-500">*</span>
            </label>
            <select
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={form.tipo_evento}
              onChange={(e) => handleChange("tipo_evento", e.target.value)}
            >
              <option value="">Seleccioná un evento...</option>
              {Object.entries(tiposEventos).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </div>

          {/* Canal + Destinatario */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">¿Cómo se envía?</label>
              <select
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={form.canal}
                onChange={(e) => handleChange("canal", e.target.value)}
              >
                {CANALES.map((c) => (
                  <option key={c.value} value={c.value} disabled={c.disabled}>
                    {c.label}{c.disabled ? " (próximamente)" : ""}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Rol objetivo</label>
              <select
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={form.destinatario_tipo}
                onChange={(e) => handleChange("destinatario_tipo", e.target.value)}
              >
                {DESTINATARIOS.map((d) => (
                  <option key={d.value} value={d.value}>{d.label}</option>
                ))}
              </select>
              {form.canal === "SMS" && (
                <p className="text-xs text-green-600 mt-1">
                  📱 El admin de cada empresa configura los números para este rol en Configuración de Alertas.
                </p>
              )}
            </div>
          </div>

          {/* Importancia + Activo */}
          <div className="grid grid-cols-2 gap-4 items-end">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Importancia</label>
              <div className="flex gap-2 flex-wrap">
                {IMPORTANCIAS.map((imp) => (
                  <button
                    key={imp.value}
                    type="button"
                    onClick={() => handleChange("importancia", imp.value)}
                    className={`px-3 py-1 rounded-full text-xs font-medium border transition ${
                      form.importancia === imp.value
                        ? imp.color + " border-transparent ring-2 ring-offset-1 ring-blue-400"
                        : "bg-white border-gray-300 text-gray-500 hover:border-gray-400"
                    }`}
                  >
                    {imp.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-3 pb-1">
              <span className="text-sm font-medium text-gray-700">Activo</span>
              <button
                type="button"
                onClick={() => handleChange("activo", !form.activo)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${form.activo ? "bg-blue-600" : "bg-gray-300"}`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${form.activo ? "translate-x-6" : "translate-x-1"}`} />
              </button>
            </div>
          </div>

          {/* Plantilla */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-sm font-medium text-gray-700">
                Mensaje del correo <span className="text-red-500">*</span>
              </label>
              {tienePlantillaSugerida && (
                <button
                  type="button"
                  onClick={usarPlantillaSugerida}
                  className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-medium transition"
                >
                  <Sparkles size={12} />
                  Usar plantilla sugerida
                </button>
              )}
            </div>
            <textarea
              ref={textareaRef}
              rows={5}
              maxLength={2000}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              placeholder="Escribí el mensaje que recibirá el destinatario, o usá la plantilla sugerida de arriba."
              value={form.plantilla_mensaje}
              onChange={(e) => handleChange("plantilla_mensaje", e.target.value)}
            />
            <p className={`text-xs text-right mt-1 ${form.plantilla_mensaje.length > 1800 ? "text-red-500" : "text-gray-400"}`}>
              {form.plantilla_mensaje.length} / 2000
            </p>
          </div>

          {/* Variables disponibles */}
          {Object.keys(variables).length > 0 && (
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <button
                type="button"
                className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 text-sm font-medium text-gray-700 hover:bg-gray-100 transition"
                onClick={() => setVariablesOpen(!variablesOpen)}
              >
                <span className="flex items-center gap-2">
                  <Zap size={14} className="text-blue-500" />
                  Insertar datos del viaje en el mensaje
                </span>
                {variablesOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </button>
              {variablesOpen && (
                <div className="px-4 py-3 space-y-3">
                  <p className="text-xs text-gray-500">
                    Hacé click en cualquier dato para insertarlo en el mensaje. El sistema lo reemplazará automáticamente al enviar.
                  </p>
                  {Object.entries(variables).map(([grupo, vars]) => (
                    <div key={grupo}>
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 capitalize">{grupo}</p>
                      <div className="flex flex-wrap gap-2">
                        {Object.keys(vars).map((varKey) => (
                          <button
                            key={varKey}
                            type="button"
                            onClick={() => insertarVariable(varKey)}
                            title={`Inserta: ${varKey}`}
                            className="px-2 py-1 bg-blue-50 text-blue-800 text-xs rounded hover:bg-blue-100 transition border border-blue-200"
                          >
                            {ETIQUETA_VARIABLE[varKey] ?? varKey}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Preview */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Eye size={14} className="text-gray-500" />
              <span className="text-sm font-medium text-gray-700">Cómo quedará el mensaje</span>
              {loadingPreview && <span className="text-xs text-gray-400">Generando...</span>}
            </div>
            <div className="min-h-[60px] bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-700 whitespace-pre-wrap">
              {preview || <span className="text-gray-400 italic">Escribí el mensaje y elegí un evento para ver cómo quedará.</span>}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t bg-gray-50 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-100 transition"
          >
            Cancelar
          </button>
          <button
            onClick={handleGuardar}
            disabled={saving}
            className="px-5 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
          >
            {saving ? "Guardando..." : regla ? "Actualizar" : "Crear regla"}
          </button>
        </div>
      </div>
    </div>
  );
}
